import React, { useState } from "react";
import VendorCategoriesView from "views/Search2.0/components/BusinessPages/VendorCategoriesView";

const Icon = {
  Phone: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z" />
    </svg>
  ),
  ChevronDown: ({ open }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  ChevronRight: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  ExternalLink: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3">
      <path d="M14 3h7v7" />
      <path d="M10 14 21 3" />
      <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
    </svg>
  ),
};

const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== "";

const firstValue = (...values) =>
  values.find((value) => hasValue(value) && String(value).trim() !== "--");

const displayValue = (value, fallback = "--") => (hasValue(value) ? String(value) : fallback);

const toList = (value) => {
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => (Array.isArray(item) ? item : [item]))
      .filter(hasValue)
      .map((item) => (
        typeof item === "object"
          ? item?.serviceName || item?.name || item?.label || item?.value
          : item
      ))
      .filter((item) => hasValue(item) && String(item).trim() !== "--")
      .map((item) => String(item).trim());
  }

  return hasValue(value) && String(value).trim() !== "--" ? [String(value).trim()] : [];
};

const getAttachments = (vendor) =>
  (Array.isArray(vendor?.attachments) ? vendor.attachments : [])
    .filter(Boolean)
    .map((attachment, index) => {
      if (typeof attachment === "string") {
        return {
          name: attachment.split("/").pop() || `Document ${index + 1}`,
          url: attachment,
        };
      }

      return {
        name:
          attachment?.Name ||
          attachment?.name ||
          attachment?.fileName ||
          attachment?.originalName ||
          `Document ${index + 1}`,
        url:
          attachment?.Content ||
          attachment?.content ||
          attachment?.url ||
          attachment?.URL ||
          attachment?.fileUrl ||
          attachment?.path ||
          "",
      };
    });

const getProjects = (vendor) => {
  const source = [
    vendor?.projects,
    vendor?.latestProjects,
    vendor?.projectDetails,
    vendor?.projectNames,
  ].find((item) => (Array.isArray(item) ? item.length > 0 : hasValue(item)));
  if (!Array.isArray(source)) return toList(source).map((projectName) => ({ projectName }));

  return source
    .map((project) => typeof project === "string"
      ? { projectName: project }
      : {
          projectName: project?.projectName || project?.name || project?.title,
          clientName: project?.clientName || project?.client || project?.customerName,
          location: project?.location || project?.address || project?.city,
          description: project?.description || project?.projectDescription,
        })
    .filter((project) => hasValue(project?.projectName));
};

const getInitials = (value) =>
  displayValue(value, "NA")
    .trim()
    .split(/\s+/)
    .map((word) => word[0]?.toUpperCase())
    .join("")
    .slice(0, 2);

const getLocation = (vendor) => {
  const location = Array.isArray(vendor?.location) ? vendor.location[0] : vendor?.location;
  if (typeof location === "string") return location;

  if (Array.isArray(vendor?.state) && vendor.state.length === 36) {
    return "All Over India";
  }

  const regions = [
    ...(Array.isArray(vendor?.country) && vendor.country.length > 0
      ? vendor.country.map((country) => `All Over ${country}`)
      : []),
    ...(Array.isArray(vendor?.state) && vendor.state.length > 0
      ? vendor.state.map((state) => `All Over ${state}`)
      : []),
    ...(Array.isArray(vendor?.selectedcities) ? vendor.selectedcities : []),
  ].filter(hasValue);

  if (regions.length > 0) return [...new Set(regions)].join(", ");

  return [
    location?.area,
    location?.city,
    location?.state,
    vendor?.addresses_city,
    vendor?.addresses_state,
  ]
    .filter(hasValue)
    .join(", ");
};

const getAddressDetails = (vendor) => {
  const address = Array.isArray(vendor?.addresses)
    ? vendor.addresses[0]
    : Array.isArray(vendor?.location)
      ? vendor.location[0]
      : vendor?.location;

  if (!address || typeof address !== "object") return [];

  return [
    { label: "Address Line 1", value: address.addressLine1 },
    { label: "Area", value: address.area },
    { label: "City", value: address.city },
    { label: "State", value: address.state },
    { label: "Pincode", value: address.pinCode },
  ].filter((item) => hasValue(item.value));
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const getRfqActivity = (vendor) => {
  const invited = Number(vendor?.invitedRfqs?.count ?? vendor?.invitedRfqs?.rfqIds?.length ?? 0) || 0;
  const submitted = Number(
    vendor?.submittedRfqs?.count ??
      vendor?.quotedRfqs?.count ??
      vendor?.quotationReceivedCount ??
      0,
  ) || 0;
  const won = Number(vendor?.awardedRfqs?.count ?? vendor?.awardedRfqs?.rfqIds?.length ?? 0) || 0;
  const wonAmount = Number(vendor?.totalOrderValue || 0) || 0;

  return { invited, submitted, won, wonAmount };
};

const getRfqTypeLabel = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "Unmapped RFQ";

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const getCategoryLabel = (category = {}) =>
  category?.categoryName || category?.label || getRfqTypeLabel(category?.rfqType);

const buildCategoryGroups = (categories = []) => {
  const safeCategories = Array.isArray(categories) ? categories : [];

  return safeCategories.reduce((groups, category) => {
    const typeLabel = category?.isFallback
      ? "Legacy RFQs"
      : getRfqTypeLabel(category?.rfqType);
    const disciplineLabel = category?.disciplineName || typeLabel;
    const groupKey = `${typeLabel}::${disciplineLabel}`;

    if (!groups[groupKey]) {
      groups[groupKey] = {
        key: groupKey,
        typeLabel,
        disciplineLabel,
        count: 0,
        categories: [],
      };
    }

    groups[groupKey].count += Number(category?.count || 0);
    groups[groupKey].categories.push(category);
    return groups;
  }, {});
};

const SectionLabel = ({ children }) => (
  <p className="mb-3 text-sm font-semibold text-[#1F2937]">
    {children}
  </p>
);

const RankRow = ({ label, count, total, color, tagClass }) => {
  const percentage = total ? Math.round((count / total) * 100) : 0;

  return (
    <div className="flex items-center gap-2">
      <span className={`flex h-5 w-9 shrink-0 items-center justify-center rounded text-[10px] font-medium ${tagClass}`}>
        {label}
      </span>
      <div className="h-2 flex-1 overflow-hidden rounded-full border border-gray-200 bg-gray-100">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${percentage}%` }} />
      </div>
      <span className="w-4 text-right text-xs font-medium text-gray-800">{count}</span>
      <span className="w-8 text-right text-[10px] text-gray-400">{percentage}%</span>
    </div>
  );
};

const OverviewTab = ({ vendor, contactVisible, onRevealContact, wideLayout = false, trustedCompaniesSection }) => {
  const [openProjectIndexes, setOpenProjectIndexes] = useState({});
  const contacts = (Array.isArray(vendor?.contactDetails) ? vendor.contactDetails : []).filter(
    (contact) =>
      hasValue(contact?.mobile) ||
      hasValue(contact?.email) ||
      (hasValue(contact?.name) && String(contact.name).trim().toLowerCase() !== "primary"),
  );
  const businessMobiles = toList(
    vendor?.businessMobileNumber ||
    vendor?.businessMobile ||
    vendor?.businessProfile?.businessMobile ||
    vendor?.contact,
  );
  const businessEmails = toList(
    vendor?.businessEmail || vendor?.businessProfile?.businessEmail,
  );
  const businessContactName = displayValue(
    firstValue(vendor?.businessName, vendor?.vendorName, vendor?.Company),
    "Business Contact",
  );
  const businessContactRows = Array.from(
    { length: Math.max(businessMobiles.length, businessEmails.length) },
    (_, index) => ({
      name: businessContactName,
      mobile: businessMobiles[index] || "",
      email: businessEmails[index] || "",
    }),
  );
  const contactRows = [
    ...businessContactRows,
    ...contacts.map((contact, index) => ({
      name: displayValue(contact?.name, `Contact ${index + 1}`),
      mobile: contact?.mobile || "",
      email: contact?.email || "",
    })),
  ];
  const turnover = firstValue(vendor?.gstTurnOverRange, vendor?.annualTurnOver);
  const companyType = firstValue(vendor?.companyType, vendor?.businessType);
  const age = firstValue(vendor?.establishedYear, vendor?.yearsInBusiness, vendor?.establishYear);
  const ageText = Number(age) > 0 ? `${new Date().getFullYear() - Number(age)} years` : vendor?.ageOfBusiness;
  /* Services are intentionally hidden from the business information drawer.
  const services = toList(
    firstValue(
      Array.isArray(vendor?.serviceOffered) && vendor.serviceOffered.length > 0
        ? vendor.serviceOffered
        : null,
      vendor?.serviceNames,
      vendor?.servicesOffering?.map((service) => service?.serviceName || service?.name),
    ),
  );
  */
  const clients = toList(vendor?.clientNames || vendor?.clients);
  const certifications = toList(vendor?.isoCertificates || vendor?.certificates || vendor?.businessProfile?.certificates);
  const projects = getProjects(vendor);
  const vendorCategories = Array.isArray(vendor?.vendorCategories)
    ? vendor.vendorCategories
    : [];
  const attachments = getAttachments(vendor);
  const toggleProjectDetails = (index) => {
    setOpenProjectIndexes((currentIndexes) => ({
      ...currentIndexes,
      [index]: !currentIndexes[index],
    }));
  };

  return (
    <div className={wideLayout ? "grid grid-cols-1 items-start gap-4 px-4 py-5 sm:px-6 md:grid-cols-2" : "space-y-4 px-5 py-5"}>
      {(hasValue(ageText) || hasValue(turnover) || hasValue(companyType)) && (
      <div>
        <div className="space-y-2 rounded-2xl border border-[#E8E6DD] bg-white/90 p-4 shadow-sm">
        <SectionLabel>Summary</SectionLabel>
        <div className={wideLayout ? "grid grid-cols-1 gap-3 sm:grid-cols-2" : "grid grid-cols-3 gap-2"}>
          {hasValue(ageText) && (
            <div>
              <p className="text-[10px] text-gray-400">Age of business</p>
              <p className="text-sm text-gray-900">{ageText}</p>
            </div>
          )}
          {hasValue(turnover) && (
            <div>
              <p className="text-[10px] text-gray-400">Turnover</p>
              <p className="text-sm text-gray-900">{turnover}</p>
            </div>
          )}
          {hasValue(companyType) && (
            <div>
              <p className="text-[10px] text-gray-400">Company type</p>
              <p className="text-sm text-gray-900">{companyType}</p>
            </div>
          )}
        </div>
        </div>
      </div>)}
{(() => {
  const addressDetails = getAddressDetails(vendor);

return addressDetails.length > 0 ? (
  <div className="rounded-2xl border border-[#E8E6DD] bg-white/90 p-4 shadow-sm">
    <SectionLabel>Address</SectionLabel>

    <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
      {addressDetails.map(({ label, value }) => (
        <div key={label} className="min-w-[100px]">
          <span className="mb-1 block text-xs font-bold text-gray-500">
            {label}
          </span>

          <span className="break-words text-sm text-gray-900">
            {value}
          </span>
        </div>
      ))}
    </div>
  </div>
) : null;
})()}

      {(vendor?.gstNumber || vendor?.panNumber) && (
        <div className="rounded-2xl border border-[#E8E6DD] bg-white/90 p-4 shadow-sm">
          <SectionLabel>Compliance</SectionLabel>
          <div className="grid grid-cols-2 gap-2 ">
            {vendor?.gstNumber && <div><p className="text-[10px] text-gray-400">GSTIN</p><p className="break-all text-xs text-gray-900">{vendor.gstNumber}</p></div>}
            {vendor?.panNumber && <div><p className="text-[10px] text-gray-400">PAN</p><p className="break-all text-xs text-gray-900">{vendor.panNumber}</p></div>}
          </div>
        </div>
      )}

      <div className={wideLayout ? "min-w-0 md:col-span-2" : undefined}>

          <div className="rounded-2xl border border-[#E8E6DD] bg-white/90 p-4 text-xs text-gray-700 shadow-sm">
            <SectionLabel>Contact</SectionLabel>

            {contactRows.length > 0 ? (
              <div className="overflow-x-auto rounded-xl border border-[#E5E7EB]">
                <table className="w-full min-w-[560px] border-collapse text-left">
                  <thead>
                    <tr className="bg-[#F8FAFC] text-[11px] font-semibold text-[#667085]">
                      <th scope="col" className="w-[58px] px-3 py-2.5 text-center">
                        Sr#
                      </th>
                      <th scope="col" className="px-3 py-2.5">Name</th>
                      <th scope="col" className="px-3 py-2.5">Contact</th>
                      <th scope="col" className="px-3 py-2.5">Email</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E7EB] bg-white">
                    {contactRows.map((contact, index) => (
                      <tr key={`${contact.email || contact.mobile || contact.name}-${index}`}>
                        <td className="px-3 py-2.5 text-center text-xs font-semibold text-[#667085]">
                          {index + 1}
                        </td>
                        <td className="px-3 py-2.5 text-sm font-medium text-[#1F2937]">
                          {contact.name}
                        </td>
                        <td className="px-3 py-2.5 text-sm text-[#4B5563]">
                          {contact.mobile ? (
                            <a
                              href={`tel:${contact.mobile}`}
                              className="hover:text-[#185FA5]"
                            >
                              {contact.mobile}
                            </a>
                          ) : "--"}
                        </td>
                        <td className="break-all px-3 py-2.5 text-sm text-[#4B5563]">
                          {contact.email ? (
                            <a
                              href={`mailto:${contact.email}`}
                              className="hover:text-[#185FA5]"
                            >
                              {contact.email}
                            </a>
                          ) : "--"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No contact details available.</p>
            )}
          </div>
      </div>

      {trustedCompaniesSection}

      {/* Services section intentionally hidden.
      {services.length > 0 && (
        <div className="min-w-0">
          <div className="rounded-2xl border border-[#E8E6DD] bg-white/90 p-4 shadow-sm">
          <SectionLabel>Services</SectionLabel>
            <ul className="grid grid-cols-2 gap-x-5 gap-y-1 pl-4 text-xs text-gray-700">
              {services.map((service, index) => <li key={`${service}-${index}`} className="list-disc">{service}</li>)}
            </ul>
          </div>
        </div>
      )}
      */}

      {clients.length > 0 && (
        <div className="min-w-0">
          <div className="rounded-2xl border border-[#E8E6DD] bg-white/90 p-4 shadow-sm">
          <SectionLabel>Clients</SectionLabel>
            <ul className="grid grid-cols-2 gap-x-5 gap-y-1 pl-4 text-xs text-gray-700">
              {clients.map((client, index) => <li key={`${client}-${index}`} className="list-disc">{client}</li>)}
            </ul>
          </div>
        </div>
      )}

      {certifications.length > 0 && (
        <div>
          <div className="rounded-2xl border border-[#E8E6DD] bg-white/90 p-4 shadow-sm">
          <SectionLabel>Certifications</SectionLabel>
            <ul className="grid grid-cols-2 gap-x-5 gap-y-1 pl-4 text-xs text-gray-700">
              {certifications.map((certificate, index) => <li key={`${certificate}-${index}`} className="list-disc">{certificate}</li>)}
            </ul>
          </div>
        </div>
      )}

      {vendorCategories.length > 0 && (
        <VendorCategoriesView
          className={`my-0 min-w-0 ${wideLayout ? "md:col-span-2" : ""}`}
          showViewOnlyBadge={false}
          vendorCategories={vendorCategories}
          showMakes
          readOnly
        />
      )}

      {attachments.length > 0 && (
        <section className={`min-w-0 rounded-2xl border border-[#E8E6DD] bg-white/90 p-4 shadow-sm ${wideLayout ? "md:col-span-2" : ""}`}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <SectionLabel>Documents</SectionLabel>
            <span className="rounded-full bg-[#F4F4EF] px-2.5 py-1 text-[11px] font-semibold text-[#667085]">
              {attachments.length}
            </span>
          </div>
          <div className={wideLayout ? "grid grid-cols-1 items-start gap-3 md:grid-cols-2" : "space-y-2"}>
            {attachments.map((attachment, index) => {
              const documentItem = (
                <>
                  <svg
                    className="h-4 w-4 shrink-0 text-[#185FA5]"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    aria-hidden="true"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
                    <path d="M14 2v6h6M8 13h8M8 17h6" />
                  </svg>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-700" title={attachment.name}>
                    {attachment.name}
                  </span>
                  {attachment.url && <Icon.ExternalLink />}
                </>
              );

              return attachment.url ? (
                <a
                  key={`${attachment.name}-${index}`}
                  href={attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-xl border border-[#EFEFE8] bg-[#FCFCFA] px-3 py-2.5 text-[#185FA5] transition-colors hover:bg-[#F4F8FC]"
                >
                  {documentItem}
                </a>
              ) : (
                <div
                  key={`${attachment.name}-${index}`}
                  className="flex items-center gap-2 rounded-xl border border-[#EFEFE8] bg-[#FCFCFA] px-3 py-2.5"
                >
                  {documentItem}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {projects.length > 0 && (
        <section className={`min-w-0 rounded-2xl border border-[#E8E6DD] bg-white/90 p-4 shadow-sm ${wideLayout ? "md:col-span-2" : ""}`}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-[#1F2937]">Latest Projects</h3>
            <span className="rounded-full bg-[#F4F4EF] px-2.5 py-1 text-[11px] font-semibold text-[#667085]">
              {projects.length}
            </span>
          </div>
          <div className={wideLayout ? "grid grid-cols-1 items-start gap-3 md:grid-cols-2" : "space-y-2"}>
            {projects.map((project, index) => {
              const isOpen = Boolean(openProjectIndexes[index]);
              const hasDetails =
                hasValue(project?.description) ||
                hasValue(project?.location) ||
                hasValue(project?.clientName);

              return (
                <div
                  key={`${project?.projectName || "project"}-${index}`}
                  className="min-w-0 overflow-hidden rounded-xl border border-[#EFEFE8] bg-[#FCFCFA]"
                >
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-3 py-2 text-left"
                    onClick={() => toggleProjectDetails(index)}
                  >
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#185FA5] text-[10px] font-semibold text-white">
                      {index + 1}
                    </span>
                    <p className="min-w-0 flex-1 break-words text-sm font-semibold leading-6 text-[#374151]">
                      {project?.projectName}
                    </p>
                    {hasDetails && (
                      <p className="shrink-0 text-xs text-blue-800">
                        {isOpen ? "Close" : "View Details"}
                      </p>
                    )}
                  </button>

                  {hasDetails && isOpen && (
                    <div className="space-y-3 border-t border-[#EFEFE8] bg-white px-3 py-3">
                      {hasValue(project?.clientName) && (
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#667085]">
                            Client Name
                          </p>
                          <p className="mt-1 break-words text-sm leading-5">
                            {project.clientName}
                          </p>
                        </div>
                      )}
                      {hasValue(project?.location) && (
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#667085]">
                            Location
                          </p>
                          <p className="mt-1 break-words text-sm leading-5">
                            {project.location}
                          </p>
                        </div>
                      )}
                      {hasValue(project?.description) && (
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#667085]">
                            Work Done
                          </p>
                          <p className="mt-1 break-words text-sm leading-5">
                            {project.description}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

const PerformanceTab = ({ vendor, onViewOrderValue, wideLayout = false }) => {
  const activity = getRfqActivity(vendor);

  return (
    <div className={wideLayout ? "grid grid-cols-1 items-start gap-5 px-4 py-5 sm:px-6" : "space-y-4 px-5 py-5"}>
      <div>
        <SectionLabel>RFQ Activity</SectionLabel>
        <div className="flex overflow-hidden rounded-2xl border-[#E8E6DD] bg-white/90 shadow-sm">
          {[ 
            [activity.invited, "RFQs invited", "bg-white", "text-gray-900"],
            [activity.submitted, "Quotes submitted", "bg-orange-50 border-x border-gray-200", "text-[#FD6600]"],
            [activity.won, "Orders won", "bg-white", "text-emerald-600"],
          ].map(([value, label, background, color], index) => (
            <button
              key={label}
              type="button"
              disabled={label !== "Orders won" || activity.wonAmount <= 0}
              onClick={() => onViewOrderValue?.(vendor)}
              className={`relative flex-1 px-2 py-3 text-center ${background} ${
                label === "Orders won" && activity.wonAmount > 0
                  ? "cursor-pointer transition hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  : "cursor-default"
              }`}
              title={
                label === "Orders won" && activity.wonAmount > 0
                  ? "View order value breakdown"
                  : undefined
              }
            >
              <p className={`text-2xl font-medium ${color}`}>{value}</p>
              {label === "Orders won" && activity.wonAmount > 0 && (
                <p className="mt-0.5 text-[10px] font-semibold leading-tight text-emerald-700">
                  ({formatCurrency(activity.wonAmount)})
                </p>
              )}
              <p className="mt-0.5 text-[10px] leading-tight text-gray-400">{label}</p>
              {index < 2 && <span className="absolute right-0 top-1/2 z-10 translate-x-1/2 -translate-y-1/2 text-gray-300"><Icon.ChevronRight /></span>}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-baseline justify-between">
          <SectionLabel>Rank breakdown</SectionLabel>
          <span className="text-[10px] text-gray-400">out of {activity.invited} RFQs</span>
        </div>
        <div className="space-y-1.5">
          <RankRow label="L1" count={vendor?.rankBreakdown?.l1?.count || 0} total={activity.invited} color="bg-emerald-500" tagClass="bg-emerald-100 text-emerald-800" />
          <RankRow label="L2" count={vendor?.rankBreakdown?.l2?.count || 0} total={activity.invited} color="bg-blue-500" tagClass="bg-blue-100 text-blue-800" />
          <RankRow label="L3" count={vendor?.rankBreakdown?.l3?.count || 0} total={activity.invited} color="bg-amber-400" tagClass="bg-amber-100 text-amber-800" />
          <RankRow label="No Q" count={Math.max(activity.invited - activity.submitted, 0)} total={activity.invited} color="bg-red-400" tagClass="bg-red-100 text-red-700" />
        </div>
      </div>

    </div>
  );
};

const CategoriesTab = ({ vendor, wideLayout = false }) => {
  const categorySelections = Array.isArray(vendor?.categorySelections)
    ? vendor.categorySelections
    : [];
  const categoryGroups = Object.values(buildCategoryGroups(categorySelections));
  const totalMappedRfqs = categorySelections.reduce(
    (sum, category) => sum + (Number(category?.count) || 0),
    0,
  );

  return (
    <div className={wideLayout ? "space-y-4 px-4 py-5 sm:px-6" : "space-y-4 px-5 py-5"}>
      <div className="mb-3 flex items-center justify-between">
        <SectionLabel>RFQ categories</SectionLabel>
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
          {totalMappedRfqs} RFQs
        </span>
      </div>

      {categoryGroups.length ? (
        <div className={wideLayout ? "grid grid-cols-1 items-start gap-4 md:grid-cols-2" : "space-y-3"}>
          {categoryGroups.map((group) => (
            <div
              key={group.key}
              className="rounded-2xl border border-[#E8E6DD] bg-white/90 p-4 shadow-sm"
            >
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold text-gray-900">
                    {group.disciplineLabel}
                  </p>
                  <p className="mt-0.5 text-[10px] text-gray-400">
                    {group.typeLabel}
                  </p>
                </div>
                <span className="rounded-full bg-[#F4F4EF] px-2.5 py-1 text-[10px] font-semibold text-[#667085]">
                  {group.count} RFQs
                </span>
              </div>

              <div className="flex flex-wrap gap-2">
                {group.categories.map((category, index) => (
                  <div
                    key={`${category?.key || getCategoryLabel(category)}-${index}`}
                    className="rounded-xl border border-[#EFEFE8] bg-[#FCFCFA] px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-gray-800">
                        {getCategoryLabel(category)}
                      </span>
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">
                        {Number(category?.count || 0)}
                      </span>
                    </div>
                    {category?.subCategories?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {category.subCategories.map((subCategory, subIndex) => (
                          <span
                            key={`${subCategory?.subCategoryId || subCategory?.subCategoryName}-${subIndex}`}
                            className="rounded-full bg-[#FFF4E8] px-2 py-0.5 text-[10px] font-medium text-[#9A3412]"
                          >
                            {subCategory?.subCategoryName || subCategory?.subCategoryId || "--"}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-[#E8E6DD] bg-white/90 p-4 text-sm text-gray-500 shadow-sm">
          No RFQ categories found for this vendor yet.
        </div>
      )}
    </div>
  );
};

const InternalVendorProfileContent = ({ vendor = {}, onViewRfq, onViewOrderValue, isMarketplaceVendor = false, trustedCompaniesSection }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [contactVisible, setContactVisible] = useState(false);
  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "performance", label: "Performance" },
    { key: "categories", label: "Categories 🏆" },
  ];
  const vendorName = vendor?.vendorName || vendor?.Company || "Vendor";
  const description = vendor?.description || vendor?.businessHeadline || "Internal vendor profile";
  const tabNavigation = (
    <div className="flex">
      {tabs.map((tab) => <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)} className={`flex-1 border-b-2 py-2.5 text-xs font-medium transition-colors ${activeTab === tab.key ? "border-[#FD6600] text-[#FD6600]" : "border-transparent text-gray-400 hover:text-gray-600"}`}>{tab.label}</button>)}
    </div>
  );
  const tabShell = (
    <div className="border-b border-[#E8E6DD] bg-[#FFF8F1] px-5">
      {tabNavigation}
    </div>
  );
  const tabContent = (
    <>
      {activeTab === "overview" && <OverviewTab vendor={vendor} contactVisible={contactVisible} onRevealContact={() => setContactVisible(true)} wideLayout={isMarketplaceVendor} trustedCompaniesSection={trustedCompaniesSection} />}
      {activeTab === "performance" && <PerformanceTab vendor={vendor} onViewOrderValue={onViewOrderValue} wideLayout={isMarketplaceVendor} />}
      {activeTab === "categories" && <CategoriesTab vendor={vendor} wideLayout={isMarketplaceVendor} />}
    </>
  );

  return (
    <>
      {!isMarketplaceVendor && 
      <div className="relative overflow-hidden bg-[#FFF8F1] px-5 pt-4">
        <div className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-[#FDBA74]/25" />
        <div className="absolute bottom-3 right-16 h-14 w-36 rounded-t-full bg-[#185FA5]/10" />
        <div className="mb-3 flex items-start justify-between">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-[#FDBA74] bg-white text-base font-bold text-[#185FA5] shadow-sm">
            {getInitials(vendorName)}
          </div>
          {/* <span className="rounded-full border border-[#FD6600] bg-white/90 px-2.5 py-1 text-[10px] font-medium text-orange-700">Internal view</span> */}
        </div>
        <h2 className="mb-1 text-xl font-semibold leading-6 tracking-tight text-[#111827]">{vendorName}</h2>
        <p className="mb-3 text-xs leading-5 text-[#667085]">{description}</p>
        <div className="mb-4 flex items-center gap-2">
          {/* {!contactVisible && <button type="button" onClick={() => setContactVisible(true)} className="inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-gray-800"><Icon.Phone /> Show contact info</button>} */}
        </div>
      </div>}
      {tabShell}
      {tabContent}
    </>
  );
};

export default InternalVendorProfileContent;
